import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { overrideAPI, payoutAPI } from '../lib/api';
import { 
  Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, 
  DollarSign, Users, FileText, Download, Plus, Search, 
  ChevronRight, RefreshCw, Eye, Trash2, Check, X
} from 'lucide-react';

// Status badge colors
const statusColors = {
  // Batch statuses
  Imported: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Processing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  Completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Archived: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  // Detail statuses
  Unallocated: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  Partially_Allocated: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  Fully_Allocated: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Excluded: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  // Allocation statuses
  Draft: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Locked: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  Voided: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  // Statement/Payment statuses
  Generated: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Scheduled: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  Sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export default function OverrideReconciliationPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [summary, setSummary] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('all');
  
  // Batches
  const [batches, setBatches] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMonth, setUploadMonth] = useState('');
  
  // Details
  const [details, setDetails] = useState([]);
  const [selectedDetails, setSelectedDetails] = useState([]);
  const [detailFilter, setDetailFilter] = useState({ batch_id: '', status: 'all', search: '' });
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // Allocations
  const [allocations, setAllocations] = useState([]);
  const [selectedAllocations, setSelectedAllocations] = useState([]);
  const [showAllocationDialog, setShowAllocationDialog] = useState(false);
  const [allocationDetail, setAllocationDetail] = useState(null);
  const [allocationForm, setAllocationForm] = useState({
    payee_type: 'Agent',
    payee_id: '',
    payee_name: '',
    allocation_amount: '',
    adjustment_reason: '',
    notes: ''
  });
  
  // Payee search
  const [payeeSearch, setPayeeSearch] = useState('');
  const [payeeResults, setPayeeResults] = useState([]);
  const [payeeSearching, setPayeeSearching] = useState(false);
  
  // Statements
  const [statements, setStatements] = useState([]);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateMonth, setGenerateMonth] = useState('');
  
  // Payments
  const [payments, setPayments] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStatement, setPaymentStatement] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: '',
    payment_method: 'Direct Deposit',
    reference_number: '',
    notes: ''
  });

  // Manual Entry
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({
    statement_month: '',
    writing_agent_name: '',
    agent_id: '',
    client_name: '',
    effective_date: '',
    payment_type: 'Other',
    override_amount: '',
    notes: ''
  });

  // Payment Types for dropdown
  const PAYMENT_TYPES = [
    { value: 'New_Business', label: 'New Business' },
    { value: 'Renewal', label: 'Renewal' },
    { value: 'Marketing_Dollars', label: 'Marketing Dollars' },
    { value: 'Adjustment', label: 'Adjustment' },
    { value: 'Other', label: 'Other' },
  ];

  // Load initial data
  useEffect(() => {
    loadDashboard();
    loadMonths();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadDashboard();
    }
  }, [selectedMonth]);

  const loadDashboard = async () => {
    try {
      const params = selectedMonth !== 'all' ? { statement_month: selectedMonth } : {};
      const res = await overrideAPI.reconciliation(params);
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  };

  const loadMonths = async () => {
    try {
      const res = await overrideAPI.months();
      setAvailableMonths(res.data || []);
    } catch (err) {
      console.error('Failed to load months:', err);
    }
  };

  const loadBatches = async () => {
    setLoading(true);
    try {
      const params = selectedMonth !== 'all' ? { statement_month: selectedMonth } : {};
      const res = await overrideAPI.batches.list(params);
      setBatches(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (detailFilter.batch_id) params.batch_id = detailFilter.batch_id;
      if (detailFilter.status !== 'all') params.status = detailFilter.status;
      if (detailFilter.search) params.search = detailFilter.search;
      if (selectedMonth !== 'all') params.statement_month = selectedMonth;
      
      const res = await overrideAPI.details.list(params);
      setDetails(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [detailFilter, selectedMonth]);

  const loadAllocations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMonth !== 'all') params.statement_month = selectedMonth;
      const res = await overrideAPI.allocations.list(params);
      setAllocations(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load allocations');
    } finally {
      setLoading(false);
    }
  };

  const loadStatements = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMonth !== 'all') params.statement_month = selectedMonth;
      const res = await overrideAPI.statements.list(params);
      setStatements(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMonth !== 'all') params.statement_month = selectedMonth;
      const res = await overrideAPI.payments.list(params);
      setPayments(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  // Tab change handler
  useEffect(() => {
    if (activeTab === 'batches') loadBatches();
    if (activeTab === 'details') loadDetails();
    if (activeTab === 'allocations') loadAllocations();
    if (activeTab === 'statements') loadStatements();
    if (activeTab === 'payments') loadPayments();
  }, [activeTab, loadDetails]);

  // Upload batch handler
  const handleUpload = async () => {
    if (!uploadFile || !uploadMonth) {
      toast.error('Please select a file and statement month');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('statement_month', uploadMonth);
      
      const res = await overrideAPI.batches.create(formData);
      toast.success('Batch created successfully');
      
      // Import details from the uploaded file
      await overrideAPI.batches.import(res.data.batch_id);
      toast.success('Details imported successfully');
      
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadMonth('');
      loadBatches();
      loadMonths();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload batch');
    } finally {
      setLoading(false);
    }
  };

  // Payee search
  const searchPayees = async (query) => {
    if (!query || query.length < 2) {
      setPayeeResults([]);
      return;
    }
    
    setPayeeSearching(true);
    try {
      const res = await payoutAPI.payees({ search: query, limit: 20 });
      setPayeeResults(res.data || []);
    } catch (err) {
      console.error('Failed to search payees:', err);
    } finally {
      setPayeeSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchPayees(payeeSearch), 300);
    return () => clearTimeout(timer);
  }, [payeeSearch]);

  // Create allocation
  const handleCreateAllocation = async () => {
    if (!allocationDetail || !allocationForm.payee_id || !allocationForm.allocation_amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await overrideAPI.allocations.create({
        detail_id: allocationDetail.detail_id,
        payee_type: allocationForm.payee_type,
        payee_id: allocationForm.payee_id,
        payee_name: allocationForm.payee_name,
        allocation_amount: parseFloat(allocationForm.allocation_amount),
        adjustment_reason: allocationForm.adjustment_reason,
        notes: allocationForm.notes
      });
      
      toast.success('Allocation created');
      setShowAllocationDialog(false);
      setAllocationDetail(null);
      setAllocationForm({
        payee_type: 'Agent',
        payee_id: '',
        payee_name: '',
        allocation_amount: '',
        adjustment_reason: '',
        notes: ''
      });
      loadDetails();
      loadAllocations();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create allocation');
    } finally {
      setLoading(false);
    }
  };

  // Approve allocations
  const handleApproveAllocations = async (action) => {
    if (selectedAllocations.length === 0) {
      toast.error('Please select allocations to ' + action);
      return;
    }
    
    setLoading(true);
    try {
      await overrideAPI.allocations.approve(selectedAllocations, action);
      toast.success(`Allocations ${action}d successfully`);
      setSelectedAllocations([]);
      loadAllocations();
      loadDetails();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} allocations`);
    } finally {
      setLoading(false);
    }
  };

  // Exclude details
  const handleExcludeDetails = async () => {
    if (selectedDetails.length === 0) {
      toast.error('Please select details to exclude');
      return;
    }
    
    setLoading(true);
    try {
      await overrideAPI.details.exclude(selectedDetails);
      toast.success('Details excluded');
      setSelectedDetails([]);
      loadDetails();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to exclude details');
    } finally {
      setLoading(false);
    }
  };

  // Create manual entry
  const handleCreateManualEntry = async () => {
    if (!manualEntryForm.statement_month || !manualEntryForm.writing_agent_name || !manualEntryForm.override_amount) {
      toast.error('Please fill in all required fields (Month, Writing Agent, Amount)');
      return;
    }
    
    const amount = parseFloat(manualEntryForm.override_amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Override amount must be a positive number');
      return;
    }
    
    setLoading(true);
    try {
      await overrideAPI.details.createManual({
        statement_month: manualEntryForm.statement_month,
        writing_agent_name: manualEntryForm.writing_agent_name.trim(),
        override_amount: amount,
        agent_id: manualEntryForm.agent_id?.trim() || null,
        client_name: manualEntryForm.client_name?.trim() || null,
        effective_date: manualEntryForm.effective_date || null,
        payment_type: manualEntryForm.payment_type,
        notes: manualEntryForm.notes?.trim() || null
      });
      
      toast.success('Manual entry created successfully');
      setShowManualEntryDialog(false);
      setManualEntryForm({
        statement_month: '',
        writing_agent_name: '',
        agent_id: '',
        client_name: '',
        effective_date: '',
        payment_type: 'Other',
        override_amount: '',
        notes: ''
      });
      loadDetails();
      loadMonths();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create manual entry');
    } finally {
      setLoading(false);
    }
  };

  // Generate statements
  const handleGenerateStatements = async () => {
    if (!generateMonth) {
      toast.error('Please select a statement month');
      return;
    }
    
    setLoading(true);
    try {
      const res = await overrideAPI.statements.generate(generateMonth);
      toast.success(`Generated ${res.data.statements_created} statements`);
      setShowGenerateDialog(false);
      setGenerateMonth('');
      loadStatements();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate statements');
    } finally {
      setLoading(false);
    }
  };

  // Create payment
  const handleCreatePayment = async () => {
    if (!paymentStatement || !paymentForm.amount || !paymentForm.payment_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await overrideAPI.payments.create({
        statement_id: paymentStatement.statement_id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number,
        notes: paymentForm.notes
      });
      
      toast.success('Payment created');
      setShowPaymentDialog(false);
      setPaymentStatement(null);
      setPaymentForm({
        amount: '',
        payment_date: '',
        payment_method: 'Direct Deposit',
        reference_number: '',
        notes: ''
      });
      loadPayments();
      loadStatements();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment');
    } finally {
      setLoading(false);
    }
  };

  // Update payment status
  const handleUpdatePaymentStatus = async (paymentId, status) => {
    setLoading(true);
    try {
      await overrideAPI.payments.updateStatus(paymentId, status);
      toast.success(`Payment marked as ${status}`);
      loadPayments();
      loadStatements();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="override-reconciliation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Override Reconciliation</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Import, allocate, and track override payouts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-40" data-testid="month-filter">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {availableMonths.map(m => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowManualEntryDialog(true)} data-testid="manual-entry-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Manual Entry
          </Button>
          <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-batch-btn">
            <Upload className="h-4 w-4 mr-2" />
            Import Override File
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="allocations" data-testid="tab-allocations">Allocations</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements">Statements</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {summary && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Total Override</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(summary.total_override_amount)}
                    </div>
                    <p className="text-xs text-gray-500">{summary.detail_count} detail lines</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Total Allocated</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(summary.total_allocated_amount)}
                    </div>
                    <p className="text-xs text-gray-500">{summary.allocation_count} allocations</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Unallocated</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">
                      {formatCurrency(summary.total_unallocated_amount)}
                    </div>
                    <p className="text-xs text-gray-500">Pending allocation</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Total Paid</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(summary.total_paid_amount)}
                    </div>
                    <p className="text-xs text-gray-500">{summary.payment_count} payments</p>
                  </CardContent>
                </Card>
              </div>

              {/* Workflow Progress */}
              <Card>
                <CardHeader>
                  <CardTitle>Reconciliation Workflow</CardTitle>
                  <CardDescription>Current status of the override reconciliation process</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
                        <FileSpreadsheet className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">Import</p>
                        <p className="text-sm text-gray-500">{summary.batch_count} batches</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 text-orange-600">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">Allocate</p>
                        <p className="text-sm text-gray-500">{summary.allocation_count} allocated</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">Statements</p>
                        <p className="text-sm text-gray-500">{summary.statement_count} generated</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-600">
                        <DollarSign className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">Payments</p>
                        <p className="text-sm text-gray-500">{formatCurrency(summary.total_paid_amount)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import New Batch
                  </Button>
                  <Button variant="outline" onClick={() => { setActiveTab('allocations'); loadAllocations(); }}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Review Allocations
                  </Button>
                  <Button variant="outline" onClick={() => setShowGenerateDialog(true)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Statements
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Batches Tab */}
        <TabsContent value="batches" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Import Batches</CardTitle>
              <Button onClick={loadBatches} variant="ghost" size="sm">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Lines</TableHead>
                    <TableHead className="text-right">Override Total</TableHead>
                    <TableHead className="text-right">Allocated</TableHead>
                    <TableHead>Imported</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map(batch => (
                    <TableRow key={batch.batch_id} data-testid={`batch-row-${batch.batch_id}`}>
                      <TableCell className="font-medium">{batch.statement_month}</TableCell>
                      <TableCell>{batch.original_filename}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[batch.status]}>{batch.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{batch.total_lines}</TableCell>
                      <TableCell className="text-right">{formatCurrency(batch.total_override_amount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(batch.total_allocated_amount)}</TableCell>
                      <TableCell>{new Date(batch.imported_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            title="View details"
                            onClick={() => {
                              setDetailFilter({ ...detailFilter, batch_id: batch.batch_id });
                              setSelectedBatch(batch);
                              setActiveTab('details');
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            title="Download source file"
                            onClick={async () => {
                              try {
                                const res = await overrideAPI.batches.download(batch.batch_id);
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = batch.original_filename;
                                a.click();
                              } catch (err) {
                                toast.error('Failed to download file');
                              }
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {batch.status !== 'Archived' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              title="Delete batch permanently"
                              className="text-gray-400 hover:text-red-600"
                              onClick={async () => {
                                if (window.confirm(`⚠️ PERMANENTLY DELETE batch "${batch.original_filename}"?\n\nThis will also delete all details and allocations. This cannot be undone.`)) {
                                  try {
                                    await overrideAPI.batches.delete(batch.batch_id);
                                    toast.success('Batch deleted');
                                    loadBatches();
                                    loadDashboard();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to delete batch');
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {batches.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No batches found. Click "Import Override File" to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Override Detail Lines</CardTitle>
                  {selectedBatch && (
                    <CardDescription>
                      Viewing batch: {selectedBatch.original_filename} ({selectedBatch.statement_month})
                    </CardDescription>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowManualEntryDialog(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Manual Entry
                  </Button>
                  <Select 
                    value={detailFilter.status} 
                    onValueChange={(v) => setDetailFilter({ ...detailFilter, status: v })}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="Imported">Imported</SelectItem>
                      <SelectItem value="Unallocated">Unallocated</SelectItem>
                      <SelectItem value="Partially_Allocated">Partially Allocated</SelectItem>
                      <SelectItem value="Fully_Allocated">Fully Allocated</SelectItem>
                      <SelectItem value="Excluded">Excluded</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search..."
                      className="pl-9 w-48"
                      value={detailFilter.search}
                      onChange={(e) => setDetailFilter({ ...detailFilter, search: e.target.value })}
                    />
                  </div>
                  <Button onClick={loadDetails} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {selectedDetails.length > 0 && (
                <div className="mb-4 flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <span className="text-sm">{selectedDetails.length} selected</span>
                  <Button size="sm" variant="destructive" onClick={handleExcludeDetails}>
                    <XCircle className="h-4 w-4 mr-1" />
                    Exclude Selected
                  </Button>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox 
                        checked={selectedDetails.length === details.length && details.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDetails(details.filter(d => d.status !== 'Excluded' && d.status !== 'Fully_Allocated').map(d => d.detail_id));
                          } else {
                            setSelectedDetails([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Writing Agent</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Effective Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Override Amt</TableHead>
                    <TableHead className="text-right">Allocated</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {details.map(detail => (
                    <TableRow key={detail.detail_id} data-testid={`detail-row-${detail.detail_id}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedDetails.includes(detail.detail_id)}
                          disabled={detail.status === 'Excluded' || detail.status === 'Fully_Allocated'}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDetails([...selectedDetails, detail.detail_id]);
                            } else {
                              setSelectedDetails(selectedDetails.filter(id => id !== detail.detail_id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{detail.writing_agent_name}</TableCell>
                      <TableCell>{detail.client_name}</TableCell>
                      <TableCell>{detail.effective_date}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{detail.payment_type?.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(detail.override_amount)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(detail.allocated_amount)}
                      </TableCell>
                      <TableCell className="text-right text-orange-600">
                        {formatCurrency(detail.remaining_amount)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[detail.status]}>
                          {detail.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {detail.status !== 'Excluded' && detail.remaining_amount > 0 && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setAllocationDetail(detail);
                                setAllocationForm({
                                  ...allocationForm,
                                  allocation_amount: detail.remaining_amount.toString()
                                });
                                setShowAllocationDialog(true);
                              }}
                              data-testid={`allocate-btn-${detail.detail_id}`}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Allocate
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-400 hover:text-red-600"
                            title="Delete detail"
                            onClick={async () => {
                              if (window.confirm(`Delete this detail line for "${detail.writing_agent_name}"?`)) {
                                try {
                                  await overrideAPI.details.delete(detail.detail_id);
                                  toast.success('Detail deleted');
                                  loadDetails();
                                  loadDashboard();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to delete detail');
                                }
                              }
                            }}
                            data-testid={`delete-detail-${detail.detail_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {details.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                        No detail lines found. Import a batch to see override lines.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Allocations Tab */}
        <TabsContent value="allocations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Allocations</CardTitle>
                <div className="flex gap-2">
                  {selectedAllocations.length > 0 && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => handleApproveAllocations('approve')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve ({selectedAllocations.length})
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleApproveAllocations('reject')}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  <Button onClick={loadAllocations} variant="ghost" size="sm">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox 
                        checked={selectedAllocations.length === allocations.filter(a => a.status === 'Draft').length && allocations.filter(a => a.status === 'Draft').length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAllocations(allocations.filter(a => a.status === 'Draft').map(a => a.allocation_id));
                          } else {
                            setSelectedAllocations([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Payee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Writing Agent</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Adjustment Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.map(alloc => (
                    <TableRow key={alloc.allocation_id} data-testid={`allocation-row-${alloc.allocation_id}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedAllocations.includes(alloc.allocation_id)}
                          disabled={alloc.status !== 'Draft'}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedAllocations([...selectedAllocations, alloc.allocation_id]);
                            } else {
                              setSelectedAllocations(selectedAllocations.filter(id => id !== alloc.allocation_id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{alloc.payee_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{alloc.payee_type}</Badge>
                      </TableCell>
                      <TableCell>{alloc.writing_agent_name}</TableCell>
                      <TableCell>{alloc.client_name}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(alloc.allocation_amount)}
                      </TableCell>
                      <TableCell>{alloc.adjustment_reason || '-'}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[alloc.status]}>{alloc.status}</Badge>
                      </TableCell>
                      <TableCell>{new Date(alloc.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {alloc.status !== 'Locked' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-400 hover:text-red-600"
                            title="Delete allocation"
                            onClick={async () => {
                              if (window.confirm(`Delete allocation for "${alloc.payee_name}"?`)) {
                                try {
                                  await overrideAPI.allocations.delete(alloc.allocation_id);
                                  toast.success('Allocation deleted');
                                  loadAllocations();
                                  loadDetails();
                                  loadDashboard();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to delete allocation');
                                }
                              }
                            }}
                            data-testid={`delete-alloc-${alloc.allocation_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {allocations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No allocations found. Create allocations from the Details tab.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statements Tab */}
        <TabsContent value="statements" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Statements</CardTitle>
                <div className="flex gap-2">
                  <Button onClick={() => setShowGenerateDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Generate Statements
                  </Button>
                  <Button onClick={loadStatements} variant="ghost" size="sm">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Total Amount</TableHead>
                    <TableHead className="text-right">Lines</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statements.map(stmt => (
                    <TableRow key={stmt.statement_id} data-testid={`statement-row-${stmt.statement_id}`}>
                      <TableCell className="font-medium">{stmt.payee_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{stmt.payee_type}</Badge>
                      </TableCell>
                      <TableCell>{stmt.statement_month}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(stmt.total_amount)}
                      </TableCell>
                      <TableCell className="text-right">{stmt.line_count}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[stmt.status]}>{stmt.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {stmt.payment_status ? (
                          <Badge className={statusColors[stmt.payment_status]}>{stmt.payment_status}</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>{new Date(stmt.generated_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {stmt.status === 'Generated' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setPaymentStatement(stmt);
                              setPaymentForm({
                                ...paymentForm,
                                amount: stmt.total_amount.toString()
                              });
                              setShowPaymentDialog(true);
                            }}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            Record Payment
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="ml-1"
                          onClick={async () => {
                            try {
                              const res = await overrideAPI.statements.downloadPdf(stmt.statement_id);
                              const url = window.URL.createObjectURL(new Blob([res.data]));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `statement_${stmt.payee_name.replace(/\s+/g, '_')}_${stmt.statement_month}.pdf`;
                              a.click();
                              toast.success('PDF downloaded');
                            } catch (err) {
                              toast.error('Failed to download PDF');
                            }
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-1 text-gray-400 hover:text-red-600"
                          title="Delete statement"
                          onClick={async () => {
                            if (window.confirm(`Delete statement for "${stmt.payee_name}" (${stmt.statement_month})?\n\nThis will unlock the allocations.`)) {
                              try {
                                await overrideAPI.statements.delete(stmt.statement_id);
                                toast.success('Statement deleted');
                                loadStatements();
                                loadAllocations();
                                loadDashboard();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to delete statement');
                              }
                            }
                          }}
                          data-testid={`delete-stmt-${stmt.statement_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {statements.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No statements found. Approve allocations and generate statements.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payments</CardTitle>
                <Button onClick={loadPayments} variant="ghost" size="sm">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payee</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map(payment => (
                    <TableRow key={payment.payment_id} data-testid={`payment-row-${payment.payment_id}`}>
                      <TableCell className="font-medium">{payment.payee_name}</TableCell>
                      <TableCell>{payment.statement_month}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell>{payment.payment_method}</TableCell>
                      <TableCell>{payment.reference_number || '-'}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[payment.status]}>{payment.status}</Badge>
                      </TableCell>
                      <TableCell>{payment.payment_date}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {payment.status === 'Scheduled' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleUpdatePaymentStatus(payment.payment_id, 'Sent')}
                            >
                              Mark Sent
                            </Button>
                          )}
                          {payment.status === 'Sent' && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleUpdatePaymentStatus(payment.payment_id, 'Paid')}
                            >
                              Mark Paid
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-400 hover:text-red-600"
                            title="Delete payment"
                            onClick={async () => {
                              if (window.confirm(`Delete payment for "${payment.payee_name}"?`)) {
                                try {
                                  await overrideAPI.payments.delete(payment.payment_id);
                                  toast.success('Payment deleted');
                                  loadPayments();
                                  loadStatements();
                                  loadDashboard();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to delete payment');
                                }
                              }
                            }}
                            data-testid={`delete-payment-${payment.payment_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {payments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No payments found. Create payments from the Statements tab.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Import Override File</DialogTitle>
            <DialogDescription>
              Upload an Excel or CSV file containing override data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Statement Month</Label>
              <Input 
                type="month" 
                value={uploadMonth}
                onChange={(e) => setUploadMonth(e.target.value)}
                data-testid="upload-month"
              />
            </div>
            <div className="space-y-2">
              <Label>Override File</Label>
              <Input 
                type="file" 
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="upload-file"
              />
              <p className="text-xs text-gray-500">
                Supported formats: CSV, Excel (.xlsx, .xls)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={loading || !uploadFile || !uploadMonth}>
              {loading ? 'Uploading...' : 'Upload & Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocation Dialog */}
      <Dialog open={showAllocationDialog} onOpenChange={setShowAllocationDialog}>
        <DialogContent data-testid="allocation-dialog">
          <DialogHeader>
            <DialogTitle>Create Allocation</DialogTitle>
            {allocationDetail && (
              <DialogDescription>
                Allocating from: {allocationDetail.writing_agent_name} - {allocationDetail.client_name}
                <br />
                Override Amount: {formatCurrency(allocationDetail.override_amount)} | 
                Remaining: {formatCurrency(allocationDetail.remaining_amount)}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Payee Type</Label>
              <Select 
                value={allocationForm.payee_type} 
                onValueChange={(v) => setAllocationForm({ ...allocationForm, payee_type: v })}
              >
                <SelectTrigger data-testid="payee-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Agent">Agent</SelectItem>
                  <SelectItem value="Agency">Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Search Payee</Label>
              <Input 
                placeholder="Type to search agents or agencies..."
                value={payeeSearch}
                onChange={(e) => setPayeeSearch(e.target.value)}
                data-testid="payee-search"
              />
              {payeeSearching && <p className="text-xs text-gray-500">Searching...</p>}
              {payeeResults.length > 0 && (
                <div className="border rounded-md max-h-40 overflow-y-auto">
                  {payeeResults.map((p, idx) => (
                    <div 
                      key={idx}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                      onClick={() => {
                        setAllocationForm({
                          ...allocationForm,
                          payee_type: p.payee_type,
                          payee_id: p.payee_id,
                          payee_name: p.payee_name
                        });
                        setPayeeSearch('');
                        setPayeeResults([]);
                      }}
                    >
                      <div className="font-medium">{p.payee_name}</div>
                      <div className="text-xs text-gray-500">{p.payee_type} - {p.payee_id}{p.agency_name ? ` (${p.agency_name})` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {allocationForm.payee_name && (
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                <p className="text-sm">Selected: <strong>{allocationForm.payee_name}</strong></p>
                <p className="text-xs text-gray-500">{allocationForm.payee_type} - {allocationForm.payee_id}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payee ID (Manual)</Label>
                <Input 
                  value={allocationForm.payee_id}
                  onChange={(e) => setAllocationForm({ ...allocationForm, payee_id: e.target.value })}
                  placeholder="NPN or Agency ID"
                />
              </div>
              <div className="space-y-2">
                <Label>Payee Name (Manual)</Label>
                <Input 
                  value={allocationForm.payee_name}
                  onChange={(e) => setAllocationForm({ ...allocationForm, payee_name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Allocation Amount *</Label>
              <Input 
                type="number"
                step="0.01"
                value={allocationForm.allocation_amount}
                onChange={(e) => setAllocationForm({ ...allocationForm, allocation_amount: e.target.value })}
                data-testid="allocation-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Adjustment Reason (if different from override)</Label>
              <Input 
                value={allocationForm.adjustment_reason}
                onChange={(e) => setAllocationForm({ ...allocationForm, adjustment_reason: e.target.value })}
                placeholder="e.g., correction, bonus, holdback"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={allocationForm.notes}
                onChange={(e) => setAllocationForm({ ...allocationForm, notes: e.target.value })}
                placeholder="Internal notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllocationDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateAllocation} 
              disabled={loading || !allocationForm.payee_id || !allocationForm.allocation_amount}
            >
              {loading ? 'Creating...' : 'Create Allocation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Statements Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate Statements</DialogTitle>
            <DialogDescription>
              Generate statements from all approved allocations for a specific month
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Statement Month</Label>
              <Select value={generateMonth} onValueChange={setGenerateMonth}>
                <SelectTrigger data-testid="generate-month">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {availableMonths.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-gray-500">
              This will create statements for all payees with approved allocations for the selected month.
              Statements are frozen once generated.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateStatements} disabled={loading || !generateMonth}>
              {loading ? 'Generating...' : 'Generate Statements'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            {paymentStatement && (
              <DialogDescription>
                Statement for: {paymentStatement.payee_name} ({paymentStatement.statement_month})
                <br />
                Total Amount: {formatCurrency(paymentStatement.total_amount)}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input 
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                data-testid="payment-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Date *</Label>
              <Input 
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                data-testid="payment-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Direct Deposit">Direct Deposit</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference Number</Label>
              <Input 
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                placeholder="Check # or transaction ID"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreatePayment} 
              disabled={loading || !paymentForm.amount || !paymentForm.payment_date}
            >
              {loading ? 'Creating...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
        <DialogContent className="max-w-lg" data-testid="manual-entry-dialog">
          <DialogHeader>
            <DialogTitle>Add Manual Override Entry</DialogTitle>
            <DialogDescription>
              Create a manual override entry for adjustments, corrections, or missing data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statement Month *</Label>
                <Input 
                  type="month" 
                  value={manualEntryForm.statement_month}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, statement_month: e.target.value })}
                  data-testid="manual-statement-month"
                />
              </div>
              <div className="space-y-2">
                <Label>Override Amount *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={manualEntryForm.override_amount}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, override_amount: e.target.value })}
                  data-testid="manual-override-amount"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Writing Agent Name *</Label>
              <Input 
                placeholder="Enter agent name"
                value={manualEntryForm.writing_agent_name}
                onChange={(e) => setManualEntryForm({ ...manualEntryForm, writing_agent_name: e.target.value })}
                data-testid="manual-writing-agent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agent ID / NPN</Label>
                <Input 
                  placeholder="Optional"
                  value={manualEntryForm.agent_id}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, agent_id: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Type</Label>
                <Select 
                  value={manualEntryForm.payment_type} 
                  onValueChange={(v) => setManualEntryForm({ ...manualEntryForm, payment_type: v })}
                >
                  <SelectTrigger data-testid="manual-payment-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPES.map(pt => (
                      <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Name</Label>
                <Input 
                  placeholder="Optional"
                  value={manualEntryForm.client_name}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, client_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input 
                  type="date"
                  value={manualEntryForm.effective_date}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, effective_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                placeholder="Reason for manual entry, adjustment details, etc."
                value={manualEntryForm.notes}
                onChange={(e) => setManualEntryForm({ ...manualEntryForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualEntryDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateManualEntry} 
              disabled={loading || !manualEntryForm.statement_month || !manualEntryForm.writing_agent_name || !manualEntryForm.override_amount}
              data-testid="manual-entry-submit"
            >
              {loading ? 'Creating...' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
